import { useState, useEffect, ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Button,
	Container,
	Col,
	Row,
	Form,
	Offcanvas,
} from "react-bootstrap";
import Modal from "../common/components/ThemedModal";
import {
	joinLeague,
	leaveLeague,
	updateLeague,
} from "../../services/leagueService";
import {
	createPrize,
	deletePrize,
	getPublicPrizes,
	PrizeRecord,
	updatePrize,
	uploadPrizeImage,
} from "../../services/prizeService";
import LeagueComments from "./Components/LeagueComments";
import { updateLeaderbordById } from "../../services/leaderboardService";
import { MdWarning } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import MobileAd from "../Ads/MobileAd";
import TopAd from "../Ads/TopAd";
import { Helmet } from "react-helmet";
import {
	initialLeaderboard,
	Leaderboard,
	LeagueWithSportsAndSeries,
} from "../../types/model";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import { Privacy } from "../../constants/constants";
import LeagueTitleCard from "./Components/LeagueTitleCard";
import {
	fetchLeagueThunk,
	fetchTeamWithLeagueIdThunk,
	getLeaderboardThunk,
} from "../../store/thunks/fantasyThunk";
import LeagueLeaderboard from "./Components/LeagueLeaderboard";
import FantasyTeam from "../Fantasy/Components/FantasyTeam";
import PrizeCard from "../Fantasy/Components/Leagues/PrizeCard";
import ThreebyThreeAd from "../Ads/ThreebyThreeAd";
import PrizeDescriptionCard from "./Components/PrizeDescriptionCard";
import ThreebySixAd from "../Ads/ThreebySixAd";

const LeaguePage = () => {
	const { leagueId } = useParams();
	const navigate = useNavigate();

	const {
		events,
		selectedTeam,
		selectedLeaderBoard,
		selectedLeague,
		seasons,
		teamLoading,
	} = useAppSelector((state: RootState) => state.fantasy);

	const { isLoggedIn } = useAppSelector((state: RootState) => state.user);
	const dispatch = useAppDispatch();

	const [showSignInModal, setShowSignInModal] = useState(false);
	const [showCreateTeam, setShowCreateTeam] = useState(false);
	const [joinLoading, setJoinLoading] = useState(false);
	const [leaveLeagueModal, setLeaveLeagueModal] = useState(false);
	const [leaderboard, setLeaderboard] =
		useState<Leaderboard>(initialLeaderboard);
	const [leaderboardLoading, setLeaderboardLoading] = useState(false);
	const [leagues, setLeagues] = useState<LeagueWithSportsAndSeries[]>([]);
	const [seasonPrize, setSeasonPrize] = useState<PrizeRecord | null>(null);
	const [roundPrize, setRoundPrize] = useState<PrizeRecord | null>(null);
	const [showEditLeagueDrawer, setShowEditLeagueDrawer] = useState(false);
	const [editLeagueData, setEditLeagueData] = useState({
		name: selectedLeague?.name ? selectedLeague.name : "",
		description: selectedLeague?.description ? selectedLeague.description : "",
		privacy: selectedLeague?.privacy ? selectedLeague.privacy : "public",
	});
	const [seasonPrizeForm, setSeasonPrizeForm] = useState({
		title: "",
		description: "",
		linkUrl: "",
		winnerLabel: "",
		isActive: true,
		image: {
			url: "",
			key: "",
		},
	});
	const [seasonPrizeSaving, setSeasonPrizeSaving] = useState(false);
	const [seasonPrizeUploading, setSeasonPrizeUploading] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	const isHardline = selectedLeague?.series?.name === "Red Bull Hardline";
	//Check if league season is active

	const userInLeague = selectedTeam?._id
		? selectedLeague?.teams?.some((team: any) => team === selectedTeam._id)
		: false;

	const mapSeasonPrizeToForm = (prize: PrizeRecord | null) => ({
		title: prize?.title || "",
		description: prize?.description || "",
		linkUrl: prize?.linkUrl || "",
		winnerLabel: prize?.winnerLabel || "",
		isActive: typeof prize?.isActive === "boolean" ? prize.isActive : true,
		image: {
			url: prize?.image?.url || "",
			key: prize?.image?.key || "",
		},
	});

	const seasonPrizeCard = seasonPrize
		? {
				description: seasonPrize.title || seasonPrize.description,
				imageUrl: seasonPrize?.image?.url || "",
				linkUrl: seasonPrize.linkUrl || "",
				winnerLabel:
					seasonPrize.winnerLabel ||
					(typeof seasonPrize.winnerSnapshot === "object"
						? seasonPrize.winnerSnapshot?.name || ""
						: ""),
		  }
		: selectedLeague?.overallPrizeDescription
			? {
					description: selectedLeague.overallPrizeDescription,
					imageUrl: selectedLeague?.image || "",
					linkUrl: selectedLeague?.overallPrizeUrl || "",
					winnerLabel: "",
			  }
			: null;

	const roundPrizeCard = roundPrize
		? {
				description: roundPrize.title || roundPrize.description,
				imageUrl: roundPrize?.image?.url || "",
				linkUrl: roundPrize.linkUrl || "",
				eventName:
					typeof roundPrize.event === "object"
						? roundPrize.event?.name || ""
						: "",
				winnerLabel:
					roundPrize.winnerLabel ||
					(typeof roundPrize.winnerSnapshot === "object"
						? roundPrize.winnerSnapshot?.name || ""
						: ""),
		  }
		: selectedLeague?.roundPrizeDescription
			? {
					description: selectedLeague.roundPrizeDescription,
					imageUrl: selectedLeague?.roundImage || "",
					linkUrl: selectedLeague?.roundPrizeUrl || "",
					eventName: "",
					winnerLabel: "",
			  }
			: null;

	const hasPrizeCards = Boolean(seasonPrizeCard || roundPrizeCard);

	useEffect(() => {
		fetchTeam();
		fetchLeagueData();
		fetchLeaderboard();
	}, [leagueId, isLoggedIn]);

	useEffect(() => {
		if (!selectedLeague?._id) {
			setSeasonPrize(null);
			setRoundPrize(null);
			return;
		}
		void fetchPrizesForLeague();
	}, [selectedLeague?._id]);

	useEffect(() => {
		if (!selectedLeague) return;
		fetchLeaderboard();
	}, [selectedLeague]);

	useEffect(() => {
		if (!selectedLeague) return;
		setEditLeagueData({
			name: selectedLeague?.name ? selectedLeague.name : "",
			description: selectedLeague?.description
				? selectedLeague.description
				: "",
			privacy: selectedLeague?.privacy ? selectedLeague.privacy : "public",
		});
	}, [selectedLeague]);

	useEffect(() => {
		if (!showEditLeagueDrawer) return;
		setSeasonPrizeForm(mapSeasonPrizeToForm(seasonPrize));
	}, [showEditLeagueDrawer, seasonPrize?._id]);


	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		handleResize(); // Initial check
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const fetchLeagueData = async () => {
		try {
			if (!leagueId) {
				console.error("League ID is required to fetch league data");
				return;
			}

			dispatch(fetchLeagueThunk(leagueId));
		} catch (error) {
			console.error("Error fetching league data:", error);
		}
	};

	const fetchPrizesForLeague = async () => {
		try {
			if (!selectedLeague?._id) {
				return;
			}
				const response = await getPublicPrizes({
					leagueId: selectedLeague._id,
					limit: 20,
				});
			const items = (response.data?.items || []).filter((item) => item.isActive);
			const season = items.find((item) => item.prizeType === "season") || null;
			const round = items.find((item) => item.prizeType === "round") || null;
			setSeasonPrize(season);
			setRoundPrize(round);
		} catch (error) {
			console.error("Error fetching league prizes:", error);
			setSeasonPrize(null);
			setRoundPrize(null);
		}
	};

	const fetchTeam = async () => {
		// Fetch the user's team in the league
		try {
			if (!leagueId) {
				console.error("League ID is required to fetch team");
				return;
			}

			dispatch(fetchTeamWithLeagueIdThunk(leagueId));
		} catch (error) {
			console.error("Error fetching team:", error);
		}
	};

	const fetchLeaderboard = async () => {
		// Fetch the leaderboard for the league
		try {
			if (!leagueId) {
				console.error("League ID is required to fetch leaderboard");
				return;
			}
			setLeaderboardLoading(true);
			dispatch(getLeaderboardThunk(leagueId));
		} catch (error) {
			console.error("Error fetching leaderboard:", error);
		} finally {
			setLeaderboardLoading(false);
		}
	};

	const handleRefreshLeaderboard = async () => {
		try {
			const response = await updateLeaderbordById(leagueId);
		} catch (error) {
			console.error("Error refreshing leaderboard:", error);
		}
	};

	const handleJoinLeague = async () => {
		try {
			if (!isLoggedIn) {
				setShowSignInModal(true);
				return;
			}
			setJoinLoading(true);
			const res = await joinLeague(leagueId);

			const response = await updateLeaderbordById(leagueId);
			fetchLeaderboard();
			fetchLeagueData();
			fetchTeam();
		} catch (error: any) {
			console.error("Error joining league:", error);

			if (error.response.status === 406) {
				setShowCreateTeam(true);
			}
		} finally {
			setJoinLoading(false);
		}
	};

	const handleLeaveLeague = async () => {
		try {
			const res = await leaveLeague(leagueId, selectedTeam?._id);
			if (res.status === 200) {
				alert("You have successfully left the league.");
				// setUserInLeague(false);
			}

			const response = await updateLeaderbordById(leagueId);
			if (response.status === 200) {
				await fetchLeaderboard();
			}
		} catch (error) {
			console.error("Error leaving league:", error);
		} finally {
			fetchLeagueData();
			fetchTeam();
		}
	};

	const handleEditLeague = async () => {
		try {
			const res = await updateLeague(leagueId, editLeagueData);
			if (res.status === 200) {
				alert("League updated successfully");
				setShowEditLeagueDrawer(false);
				fetchLeagueData();
			}
		} catch (error) {
			console.error("Error editing league:", error);
		}
	};

	const handleOpenEditLeagueDrawer = () => {
		setEditLeagueData({
			name: selectedLeague?.name ? selectedLeague.name : "",
			description: selectedLeague?.description
				? selectedLeague.description
				: "",
			privacy: selectedLeague?.privacy ? selectedLeague.privacy : "public",
		});
		setSeasonPrizeForm(mapSeasonPrizeToForm(seasonPrize));
		setShowEditLeagueDrawer(true);
	};

	const handleSeasonPrizeImageUpload = async (
		event: ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file || !leagueId) {
			return;
		}
		try {
			setSeasonPrizeUploading(true);
			const response = await uploadPrizeImage(file, leagueId);
			setSeasonPrizeForm((prev) => ({
				...prev,
				image: {
					url: response.data?.url || "",
					key: response.data?.key || "",
				},
			}));
		} catch (error: any) {
			console.error("Failed to upload prize image:", error);
			alert(
				typeof error?.response?.data === "string"
					? error.response.data
					: "Failed to upload prize image.",
			);
		} finally {
			setSeasonPrizeUploading(false);
			event.target.value = "";
		}
	};

	const handleSaveSeasonPrize = async () => {
		if (!leagueId) {
			alert("League is required.");
			return;
		}
		if (!seasonPrizeForm.title.trim()) {
			alert("Season prize title is required.");
			return;
		}
		if (!seasonPrizeForm.description.trim()) {
			alert("Season prize description is required.");
			return;
		}

		const payload = {
			league: leagueId,
			prizeType: "season" as const,
			event: null,
			title: seasonPrizeForm.title.trim(),
			description: seasonPrizeForm.description.trim(),
			linkUrl: seasonPrizeForm.linkUrl.trim(),
			image: seasonPrizeForm.image,
			winnerLabel: seasonPrizeForm.winnerLabel.trim(),
			isActive: seasonPrizeForm.isActive,
		};

		try {
			setSeasonPrizeSaving(true);
			if (seasonPrize?._id) {
				await updatePrize(seasonPrize._id, payload);
			} else {
				await createPrize(payload);
			}
			await fetchPrizesForLeague();
			alert("Season prize saved.");
		} catch (error: any) {
			console.error("Failed to save season prize:", error);
			alert(
				typeof error?.response?.data === "string"
					? error.response.data
					: "Failed to save season prize.",
			);
		} finally {
			setSeasonPrizeSaving(false);
		}
	};

	const handleDeactivateSeasonPrize = async () => {
		if (!seasonPrize?._id) {
			return;
		}
		if (!window.confirm("Deactivate this season prize?")) {
			return;
		}
		try {
			await deletePrize(seasonPrize._id);
			await fetchPrizesForLeague();
			setSeasonPrizeForm(mapSeasonPrizeToForm(null));
		} catch (error: any) {
			console.error("Failed to deactivate season prize:", error);
			alert(
				typeof error?.response?.data === "string"
					? error.response.data
					: "Failed to deactivate season prize.",
			);
		}
	};

	return (
		<Container
			className="league-page w-100 text-center"
			style={{ overflow: "hidden" }}
		>
			<Helmet>
				<title>League - The Race Companion</title>
				<meta
					name="description"
					content="The Race Companion League Page. Compete against friends with teams built around Enduro, Slopestyle, Freeride or Downhill Mountain Biking. Follow along with Crankworx, World cups, Red Bull Hardline and Red Bull Rampage."
				/>
				<meta name="viewport" content="width=device-width, initial-scale=1" />
			</Helmet>
			{/* <Row className="m-0 p-0">{isMobile ? <MobileAd /> : <TopAd />}</Row> */}
				<Row className="mb-3">
					<Col lg={5} md={8} sm={12}>
						<LeagueTitleCard
							league={selectedLeague}
							handleEditLeague={handleOpenEditLeagueDrawer}
							handleJoinLeague={handleJoinLeague}
							handleLeaveLeague={() => setLeaveLeagueModal(true)}
							showJoinButton={!userInLeague}
							joinLoading={joinLoading}
						/>
					</Col>
					{hasPrizeCards ? (
						<>
							<Col
								lg={4}
								md={6}
								sm={12}
								className="d-flex justify-content-md-end justify-content-center"
							>
								{seasonPrizeCard ? (
									<PrizeCard
										imageUrl={seasonPrizeCard.imageUrl}
										prizeType="season"
										brandUrl={seasonPrizeCard.linkUrl}
										description={seasonPrizeCard.description}
										series={selectedLeague?.series}
										sport={selectedLeague?.sport}
										winnerLabel={seasonPrizeCard.winnerLabel}
									/>
								) : (
									<></>
								)}
							</Col>
							<Col
								lg={3}
								md={6}
								sm={12}
								className="d-flex justify-content-md-end justify-content-center"
							>
								{roundPrizeCard ? (
									<PrizeCard
										imageUrl={roundPrizeCard.imageUrl}
										prizeType="round"
										brandUrl={roundPrizeCard.linkUrl}
										description={roundPrizeCard.description}
										series={selectedLeague?.series}
										sport={selectedLeague?.sport}
										eventName={roundPrizeCard.eventName}
										winnerLabel={roundPrizeCard.winnerLabel}
									/>
								) : (
									<>{!isMobile ? <ThreebySixAd /> : <ThreebyThreeAd />}</>
								)}
							</Col>
						</>
					) : (
					<>
						<Col
							lg={4}
							md={4}
							sm={12}
							className="d-flex justify-content-md-center justify-content-center my-4"
						>
							{selectedLeague?.prize || selectedLeague?.losing_prize ? (
								<PrizeDescriptionCard
									winningPrize={selectedLeague?.prize ?? ""}
									losingPrize={selectedLeague?.losing_prize ?? ""}
								/>
							) : (
								<ThreebyThreeAd />
							)}
						</Col>
						<Col
							lg={3}
							md={3}
							sm={12}
							className="d-flex justify-content-md-end justify-content-center my-4"
						>
							<ThreebyThreeAd />
						</Col>
					</>
				)}
			</Row>

			<Row className="mb-4 align-items-start">
				<Col lg={8} md={12} sm={12} className="mb-4 mb-lg-0">
					<LeagueLeaderboard />
				</Col>
				<Col lg={4} md={12} sm={12}>
					<LeagueComments leagueId={leagueId} />
				</Col>
			</Row>
			{selectedTeam?._id ? (
				<Row className="mb-4">
					<Col lg={12} md={12} sm={12}>
						<FantasyTeam
							team={selectedTeam}
							isLoading={teamLoading}
							showLeagues={false}
							showLeagueSeries={true}
							showLeagueSport={true}
						/>
					</Col>
				</Row>
			) : null}

			<Modal show={showSignInModal} onHide={() => setShowSignInModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Sign In Required</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>You must be signed in to join a league.</p>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={() => setShowSignInModal(false)}>
						Close
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal show={showCreateTeam} onHide={() => setShowCreateTeam(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Create Team</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						You must create have a team in{" "}
						<b>
							{selectedLeague?.series.name}: {selectedLeague?.sport.name}
						</b>
					</p>
					<div className="d-flex justify-content-center">
						<Button
							variant="primary"
							className="px-4 py-2"
							onClick={() => {
								updateLeaderbordById(selectedLeague?._id);
								navigate(
									`/EditTeam?leagueId=${leagueId}&sport=${selectedLeague?.sport?._id}&series=${selectedLeague?.series._id}`,
								);
							}}
						>
							Create Team
						</Button>
					</div>
				</Modal.Body>
			</Modal>
			<Modal show={leaveLeagueModal} onHide={() => setLeaveLeagueModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						<MdWarning className="mb-1 me-2" /> Leave League
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>Are you sure you want to leave this league?</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="outline-secondary"
						onClick={() => setLeaveLeagueModal(false)}
					>
						Cancel
					</Button>
					<Button
						variant="dark"
						onClick={() => {
							handleLeaveLeague();
							setLeaveLeagueModal(false);
						}}
					>
						Leave League
					</Button>
				</Modal.Footer>
			</Modal>
			<Offcanvas
				show={showEditLeagueDrawer}
				onHide={() => setShowEditLeagueDrawer(false)}
				placement="end"
				className="admin-offcanvas league-edit-offcanvas"
			>
				<Offcanvas.Header closeButton>
					<Offcanvas.Title>
						<FaEdit className="mb-1 me-2" /> Edit League
					</Offcanvas.Title>
				</Offcanvas.Header>
				<Offcanvas.Body>
					<div className="text-start">
						<h5 className="mb-3">League Details</h5>
						<Form.Group className="mb-3">
							<Form.Label>League Name</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter league name"
								value={editLeagueData.name}
								onChange={(e) =>
									setEditLeagueData({
										...editLeagueData,
										name: e.target.value,
									})
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Label>League Description</Form.Label>
							<Form.Control
								as="textarea"
								rows={3}
								placeholder="Enter league description"
								value={editLeagueData.description}
								onChange={(e) =>
									setEditLeagueData({
										...editLeagueData,
										description: e.target.value,
									})
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Label>Privacy</Form.Label>
							<Form.Select
								value={editLeagueData.privacy}
								onChange={(e) =>
									setEditLeagueData({
										...editLeagueData,
										privacy: e.target.value as Privacy.Public | Privacy.Private,
									})
								}
							>
								<option value={Privacy.Public}>Public</option>
								<option value={Privacy.Private}>Private</option>
							</Form.Select>
						</Form.Group>
						<div className="d-flex justify-content-end mb-4">
							<Button variant="dark" onClick={handleEditLeague}>
								Save League
							</Button>
						</div>

						<hr className="my-4" />

						<div className="d-flex justify-content-between align-items-center mb-3">
							<h5 className="mb-0">Season Prize</h5>
							<span className="text-muted small">
								Only season prize can be managed here
							</span>
						</div>
						<Form.Group className="mb-3">
							<Form.Label>Title</Form.Label>
							<Form.Control
								type="text"
								placeholder="Season prize title"
								value={seasonPrizeForm.title}
								onChange={(e) =>
									setSeasonPrizeForm((prev) => ({
										...prev,
										title: e.target.value,
									}))
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Label>Description</Form.Label>
							<Form.Control
								as="textarea"
								rows={4}
								placeholder="Describe the season prize"
								value={seasonPrizeForm.description}
								onChange={(e) =>
									setSeasonPrizeForm((prev) => ({
										...prev,
										description: e.target.value,
									}))
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Label>Prize Link</Form.Label>
							<Form.Control
								type="url"
								placeholder="https://..."
								value={seasonPrizeForm.linkUrl}
								onChange={(e) =>
									setSeasonPrizeForm((prev) => ({
										...prev,
										linkUrl: e.target.value,
									}))
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Label>Winner Label</Form.Label>
							<Form.Control
								type="text"
								placeholder="Optional winner label"
								value={seasonPrizeForm.winnerLabel}
								onChange={(e) =>
									setSeasonPrizeForm((prev) => ({
										...prev,
										winnerLabel: e.target.value,
									}))
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Label>Prize Image</Form.Label>
							<Form.Control
								type="file"
								accept="image/*"
								onChange={handleSeasonPrizeImageUpload}
								disabled={seasonPrizeUploading}
							/>
							<div className="small text-muted mt-1">
								{seasonPrizeUploading ? "Uploading image..." : "Upload a new image to replace the current one."}
							</div>
							{seasonPrizeForm.image.url ? (
								<img
									src={seasonPrizeForm.image.url}
									alt="Season prize"
									className="league-season-prize-preview mt-2"
								/>
							) : null}
						</Form.Group>
						<Form.Check
							className="mb-3"
							type="switch"
							label="Season prize is active"
							checked={seasonPrizeForm.isActive}
							onChange={(e) =>
								setSeasonPrizeForm((prev) => ({
									...prev,
									isActive: e.target.checked,
								}))
							}
						/>
						<div className="d-flex justify-content-between align-items-center mt-4">
							{seasonPrize?._id ? (
								<Button
									variant="outline-danger"
									onClick={handleDeactivateSeasonPrize}
								>
									Deactivate Prize
								</Button>
							) : (
								<div />
							)}
							<Button
								variant="dark"
								onClick={handleSaveSeasonPrize}
								disabled={seasonPrizeSaving}
							>
								{seasonPrizeSaving ? "Saving..." : "Save Season Prize"}
							</Button>
						</div>
					</div>
				</Offcanvas.Body>
			</Offcanvas>
		</Container>
	);
};

export default LeaguePage;
